import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Calendar, Clock, Phone, Loader2, ChevronRight } from 'lucide-react';
import { BUSINESS } from '@/lib/businessData';
import { format } from 'date-fns';
import Seo from '@/components/Seo';

export default function BlogPostPage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const load = async () => {
      setLoading(true);
      try {
        const results = await base44.entities.Article.filter({ slug }, null, 1);
        if (results.length > 0) {
          const found = results[0];
          let html = found.content_html || '';
          // When the article body was too large for an entity field, the webhook
          // stored it as an uploaded file and kept only its URL here — fetch the HTML.
          if (/^https?:\/\//.test(html) && !html.includes('<')) {
            try {
              const res = await fetch(html);
              html = await res.text();
            } catch (e) {
              html = '';
            }
          }
          setArticle({ ...found, content_html: html });
          const rel = await base44.entities.Article.list('-published_at', 4);
          setRelated(rel.filter((p) => p.id !== found.id).slice(0, 3));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  if (loading) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </main>
        <Footer />
      </>
    );
  }

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex flex-col items-center justify-center px-4">
          <h1 className="font-heading font-black text-3xl text-foreground mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-6">The article you're looking for doesn't exist.</p>
          <Link to="/blog" className="bg-primary text-background px-6 py-3 font-heading font-bold text-sm uppercase tracking-wider hover:amber-glow transition-all">
            Back to Blog
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const date = article.published_at ? format(new Date(article.published_at), 'MMMM d, yyyy') : '';
  const seoTitle = article.meta_title || `${article.title} | Mike's Garage Door Repair Blog`;
  const seoDesc = article.meta_description || article.subtitle || '';

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={`/blog/${article.slug}`}
        jsonLd={article.json_ld_schema}
      />
      <Navbar />
      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="bg-card border-b border-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav className="flex items-center gap-2 font-mono text-xs text-muted-foreground" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/blog" className="hover:text-primary transition-colors">Blog</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-foreground truncate">{article.category}</span>
            </nav>
          </div>
        </div>

        {/* Article header */}
        <article>
          <header className="bg-background border-b border-border tech-grid">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
              <div className="flex items-center gap-3 font-mono text-xs text-muted-foreground mb-4">
                {article.category && (
                  <span className="bg-primary text-background px-3 py-1 uppercase tracking-wider font-bold">{article.category}</span>
                )}
                {date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {date}
                  </span>
                )}
                {article.read_time && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {article.read_time}
                  </span>
                )}
              </div>
              <h1 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-foreground text-balance leading-[0.95]">
                {article.title}
              </h1>
              {article.subtitle && (
                <p className="mt-6 text-xl text-muted-foreground leading-relaxed">
                  {article.subtitle}
                </p>
              )}
              <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-8 h-8 bg-primary text-background flex items-center justify-center font-heading font-bold text-sm">
                  {(article.author_name || 'Mike').charAt(0)}
                </div>
                <span>By {article.author_name || 'Mike'} — Mike's Garage Door Repair, Cypress TX</span>
              </div>
            </div>
          </header>

          {/* Hero image */}
          {article.image_url && (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-1">
              <img
                src={article.image_url}
                alt={article.title}
                className="w-full h-64 sm:h-96 object-cover border border-border"
                loading="lazy"
              />
            </div>
          )}

          {/* Article body — raw HTML supplied by the external service */}
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
            <div
              className="prose prose-invert max-w-none article-content [&_a]:text-primary [&_a]:underline [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-2xl [&_h2]:text-foreground [&_h2]:mt-10 [&_h2]:mb-4 [&_h3]:font-heading [&_h3]:font-bold [&_h3]:text-xl [&_h3]:text-foreground [&_h3]:mt-8 [&_h3]:mb-3 [&_p]:text-muted-foreground [&_p]:leading-relaxed [&_p]:text-lg [&_p]:mb-4 [&_ul]:text-muted-foreground [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:text-muted-foreground [&_ol]:list-decimal [&_ol]:pl-6 [&_img]:rounded [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:italic"
              dangerouslySetInnerHTML={{ __html: article.content_html || '' }}
            />

            {/* Tags */}
            {Array.isArray(article.tags) && article.tags.length > 0 && (
              <div className="mt-10 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span key={tag} className="bg-muted text-muted-foreground px-3 py-1 font-mono text-xs uppercase tracking-wider">
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* CTA box */}
            <div className="mt-12 bg-card border border-primary/30 p-6 lg:p-8 text-center">
              <h2 className="font-heading font-bold text-2xl text-foreground mb-2">
                Need Garage Door Repair in Cypress, TX?
              </h2>
              <p className="text-muted-foreground mb-6">
                Mike's team is available 24/7 for emergency repairs. Call now for a free estimate.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href={`tel:${BUSINESS.phoneRaw}`}
                  className="flex items-center justify-center gap-2 bg-primary text-background px-6 py-4 font-heading font-bold text-sm uppercase tracking-wider hover:amber-glow transition-all"
                >
                  <Phone className="w-5 h-5" />
                  {BUSINESS.phone}
                </a>
                <Link
                  to="/#booking"
                  className="flex items-center justify-center gap-2 border-2 border-border text-foreground px-6 py-4 font-heading font-bold text-sm uppercase tracking-wider hover:border-primary hover:text-primary transition-all"
                >
                  Book Online
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related posts */}
        {related.length > 0 && (
          <section className="bg-card border-t border-border py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-heading font-bold text-2xl text-foreground mb-8">More Garage Door Tips</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {related.map((rp) => (
                  <Link
                    key={rp.id}
                    to={`/blog/${rp.slug}`}
                    className="group bg-background border border-border p-5 hover:border-primary/50 transition-all"
                  >
                    <div className="font-mono text-[10px] text-primary uppercase tracking-wider mb-2">{rp.category}</div>
                    <h3 className="font-heading font-bold text-base text-foreground group-hover:text-primary transition-colors leading-snug mb-2">
                      {rp.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{rp.subtitle || rp.meta_description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}